import { useMemo, useState } from "react";
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
  PaperClipOutlined,
  FileExcelOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { useSidebar } from "../../hooks/useSidebar";
import { useCachedList } from "../../hooks/useCachedList";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import AttachmentsManager from "../../Components/AttachmentsManager";
import { CatalogSelect } from "../../Components/CatalogTags";
import GeoSelect, { type GeoValue } from "../../Components/GeoSelect";
import ZonaCell from "../../Components/ZonaCell";
import {
  agendaMaestrosApi,
  type Maestro,
  type MaestroCreate,
} from "../../services/agendaMaestros.api";
import { catalogosApi, type CatalogoTag } from "../../services/catalogos.api";
import { exportToXlsxSingleSheet } from "../finanzas-empresa/exportXls";
import { tagColor } from "../../utils/tagColors";
import { formatPhoneDisplay } from "../../utils/formatPhone";
import { norm } from "../../utils/fuzzyMatch";
import { getMsg } from "../../utils/antdStatic";

import { paginacionCliente } from "../../utils/tablaPaginacion";
const { Text, Paragraph } = Typography;

/** Detiene la propagación del click para que celdas interactivas (links, botones)
 *  no disparen el expand de la fila (expandRowByClick). */
const stopClick = (e: React.MouseEvent) => e.stopPropagation();

/**
 * Página /agenda-contactos — "Agenda de contactos" (antes "Agenda de maestros",
 * renombre 2026-07-02). La URL vieja /agenda-maestros redirige acá y la API
 * vieja /api/agenda-maestros queda como alias; el código (carpeta, modelo,
 * tabla agenda_maestros, carpeta de storage) conserva el identificador técnico.
 *
 * Directorio interno (admin/superadmin) de gasfíteres, electricistas, cerrajeros,
 * maestros, pintores, fleteros, etc. Round 2026-06-30: rubros MÚLTIPLES desde el
 * catálogo (chips), geografía estructurada (región/comuna/sector), filas
 * expandibles, filtro por rubro, export a Excel y reorden manual.
 *
 * Ver docs/modulo-agenda-maestros.md.
 */

/** Nombres de rubro de un maestro (soporta el legacy `rubro` único). */
function rubrosOf(m: Maestro): string[] {
  if (Array.isArray(m.rubros) && m.rubros.length) return m.rubros;
  return m.rubro ? [m.rubro] : [];
}

/** Texto compacto de la zona (región › comuna › sector) o la ubicación libre. */
function zonaText(m: Maestro): string {
  const parts = [m.regionNombre, m.comunaNombre, m.sectorNombre].filter(Boolean);
  if (parts.length) return parts.join(" › ");
  return m.ubicacion || "";
}

export default function AgendaMaestrosPage() {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  // Búsqueda y rubros se recuerdan por usuario admin en este navegador
  // (round 2026-09-24, ver docs/filtros-persistentes.md). Ojo: con cualquiera
  // de los dos puesto, `canReorder` deshabilita las flechas de reordenar — el
  // tooltip de los botones ya explica por qué.
  const [search, setSearch] = useFiltroPersistido("agendaContactos.busqueda", "", {
    validar: (v) => typeof v === "string",
  });
  const [filterRubros, setFilterRubros] = useFiltroPersistido<string[]>(
    "agendaContactos.rubros",
    [],
    { validar: (v) => Array.isArray(v) && v.every((x) => typeof x === "string") },
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Maestro | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [rubrosVal, setRubrosVal] = useState<string[]>([]);
  const [geoVal, setGeoVal] = useState<GeoValue>({ region: null, comuna: null, sector: null });

  // PERF (round 2026-07-02): stale-while-revalidate — mismo patrón que
  // /costos-sugeridos. Lista y catálogo de rubros se pintan al instante desde
  // sessionStorage y se revalidan en segundo plano; spinner solo en la primera
  // visita. Ver hooks/useCachedList.
  const {
    data,
    loading,
    refresh: fetchList,
    setData,
  } = useCachedList<Maestro>(
    "cache:agenda-contactos:v1",
    async () => (await agendaMaestrosApi.list())?.data ?? null,
  );
  // Catálogo de rubros (para colorear chips y poblar el filtro).
  const { data: rubroTags, refresh: fetchRubroTags } = useCachedList<CatalogoTag>(
    "cache:catalogo:rubro:v1",
    async () => (await catalogosApi.list("rubro"))?.data ?? null,
  );

  const colorByRubro = useMemo(() => {
    const m = new Map<string, string | null | undefined>();
    for (const t of rubroTags) m.set(t.nombre, t.color);
    return m;
  }, [rubroTags]);

  const canReorder = !search.trim() && filterRubros.length === 0;

  const filtered = useMemo(() => {
    const q = norm(search);
    // Solo dígitos, para que el teléfono matchee aunque se tipee/copie con
    // los espacios del display (la DB lo guarda "pegado") — aud. 2026-07-13.
    const qDigits = search.replace(/\D/g, "");
    return data.filter((m) => {
      if (filterRubros.length && !rubrosOf(m).some((r) => filterRubros.includes(r))) return false;
      if (!q) return true;
      const hay = norm(
        [
          m.nombre,
          ...rubrosOf(m),
          m.telefono,
          m.email,
          m.ubicacion,
          zonaText(m),
          m.caracteristicas,
          m.notas,
        ]
          .filter(Boolean)
          .join(" "),
      );
      if (hay.includes(q)) return true;
      return Boolean(
        qDigits &&
          m.telefono &&
          String(m.telefono).replace(/\D/g, "").includes(qDigits),
      );
    });
  }, [data, search, filterRubros]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setRubrosVal([]);
    setGeoVal({ region: null, comuna: null, sector: null });
    setModalOpen(true);
  };

  const openEdit = (m: Maestro) => {
    setEditing(m);
    form.setFieldsValue({
      nombre: m.nombre,
      telefono: m.telefono ?? "",
      email: m.email ?? "",
      ubicacion: m.ubicacion ?? "",
      caracteristicas: m.caracteristicas ?? "",
      notas: m.notas ?? "",
    });
    setRubrosVal(rubrosOf(m));
    setGeoVal({ region: m.region ?? null, comuna: m.comuna ?? null, sector: m.sector ?? null });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
    void fetchList();
    void fetchRubroTags(); // refresca chips/filtro por si se gestionaron rubros
  };

  const submit = async () => {
    if (submitting) return;
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload: MaestroCreate = {
        nombre: (values.nombre ?? "").trim(),
        rubros: rubrosVal,
        telefono: (values.telefono ?? "").trim() || null,
        email: (values.email ?? "").trim() || null,
        ubicacion: (values.ubicacion ?? "").trim() || null,
        caracteristicas: (values.caracteristicas ?? "").trim() || null,
        notas: (values.notas ?? "").trim() || null,
        region: geoVal.region,
        comuna: geoVal.comuna,
        sector: geoVal.sector,
      };
      // Guardar y CERRAR (pedido del dueño 2026-07-02): antes el modal quedaba
      // abierto tras crear (pasaba a modo edición para adjuntar archivos), lo
      // que confundía sobre si se guardó o no. Ahora cierra en éxito; para
      // adjuntar fotos/documentos se reabre con Editar. Si el guardado falla
      // (apiFetch devuelve null + toast de error), el modal queda abierto con
      // lo tipeado. No usamos closeModal() aquí porque su guard
      // `if (submitting) return` lo anularía (submitting sigue true hasta el
      // finally).
      const res = editing
        ? await agendaMaestrosApi.update(editing.id, payload)
        : await agendaMaestrosApi.create(payload);
      if (res?.data) {
        if (!editing) {
          getMsg()?.success(
            "Contacto creado. Para adjuntar fotos o documentos, ábrelo con Editar.",
          );
        }
        setModalOpen(false);
        setEditing(null);
        form.resetFields();
        await fetchList();
        void fetchRubroTags();
      }
    } catch (err: any) {
      if (!err?.errorFields) getMsg()?.error("Error al guardar el contacto");
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id: number) => {
    const res = await agendaMaestrosApi.remove(id);
    if (res) await fetchList();
  };

  // Reorden: mueve el maestro dentro del array completo y persiste el nuevo orden.
  const move = async (id: number, dir: -1 | 1) => {
    const idx = data.findIndex((m) => m.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= data.length) return;
    const next = [...data];
    [next[idx], next[j]] = [next[j], next[idx]];
    setData(next);
    await agendaMaestrosApi.reorder(next.map((m) => m.id));
  };

  const exportExcel = () => {
    void exportToXlsxSingleSheet({
      filename: "agenda-contactos",
      sheetName: "Contactos",
      rows: filtered.map((m) => ({
        Nombre: m.nombre,
        Rubros: rubrosOf(m).join(", "),
        Teléfono: formatPhoneDisplay(m.telefono),
        Email: m.email ?? "",
        Zona: zonaText(m),
        Características: m.caracteristicas ?? "",
        Notas: m.notas ?? "",
        Adjuntos: Array.isArray(m.archivos) ? m.archivos.length : 0,
      })),
    });
  };

  const rubroChips = (m: Maestro) => {
    const rs = rubrosOf(m);
    if (!rs.length) return <Text type="secondary">—</Text>;
    return (
      <Space size={[4, 4]} wrap>
        {rs.map((r) => (
          <Tag key={r} color={tagColor(r, colorByRubro.get(r))} style={{ margin: 0 }}>
            {r}
          </Tag>
        ))}
      </Space>
    );
  };

  const columns: ColumnsType<Maestro> = [
    {
      title: "Nombre",
      dataIndex: "nombre",
      key: "nombre",
      sorter: (a, b) => (a.nombre ?? "").localeCompare(b.nombre ?? ""),
      render: (v: string) => <b>{v}</b>,
    },
    {
      title: "Rubros",
      key: "rubros",
      width: 220,
      render: (_, m) => rubroChips(m),
    },
    {
      title: "Zona",
      key: "zona",
      width: 190,
      render: (_, m) => (
        <ZonaCell
          regionNombre={m.regionNombre}
          comunaNombre={m.comunaNombre}
          sectorNombre={m.sectorNombre}
          maxWidth={155}
        />
      ),
    },
    {
      title: "Teléfono",
      dataIndex: "telefono",
      key: "telefono",
      width: 140,
      render: (v: string) =>
        v ? (
          <a href={`tel:${v.replace(/\s+/g, "")}`} onClick={stopClick}>
            {formatPhoneDisplay(v)}
          </a>
        ) : (
          "—"
        ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 180,
      render: (v: string) =>
        v ? (
          <a href={`mailto:${v}`} onClick={stopClick}>
            {v}
          </a>
        ) : (
          "—"
        ),
    },
    {
      title: "Características",
      dataIndex: "caracteristicas",
      key: "caracteristicas",
      ellipsis: true,
      render: (v: string) =>
        v ? (
          <Tooltip title={v}>
            <Text ellipsis style={{ maxWidth: 220 }}>
              {v}
            </Text>
          </Tooltip>
        ) : (
          "—"
        ),
    },
    {
      title: "Adj.",
      key: "archivos",
      width: 70,
      align: "center",
      render: (_, m) => {
        const n = Array.isArray(m.archivos) ? m.archivos.length : 0;
        return n > 0 ? (
          <Tag icon={<PaperClipOutlined />} color="geekblue">
            {n}
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "Orden",
      key: "orden",
      width: 76,
      align: "center",
      render: (_, m) => {
        const idx = data.findIndex((x) => x.id === m.id);
        return (
          <Space size={0} onClick={stopClick}>
            <Tooltip title={canReorder ? "Subir" : "Quita búsqueda/filtros para reordenar"}>
              <Button
                type="text"
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={!canReorder || idx <= 0}
                onClick={() => move(m.id, -1)}
              />
            </Tooltip>
            <Tooltip title="Bajar">
              <Button
                type="text"
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={!canReorder || idx >= data.length - 1}
                onClick={() => move(m.id, 1)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: "Acciones",
      key: "acciones",
      fixed: "right",
      width: 120,
      render: (_, m) => (
        <Space onClick={stopClick}>
          <Tooltip title="Editar">
            <Button icon={<EditOutlined />} onClick={() => openEdit(m)} />
          </Tooltip>
          <Popconfirm
            title="Eliminar contacto"
            description="¿Seguro? Se borrarán también sus archivos adjuntos."
            okText="Eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            onConfirm={() => onDelete(m.id)}
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="home-content"
    >
      <div className="finanzas-page">
        <PageHeader
          title="Agenda de contactos"
          helpText="Directorio de gente de oficio y apoyo (gasfíteres, electricistas, cerrajeros, maestros, fleteros, etc.). Cada contacto puede tener varios rubros, zona (región/comuna/sector), características y adjuntos. Usa el engranaje del selector de rubros para crear/editar/ordenar las etiquetas."
        />

        <Space style={{ marginBottom: 16, width: "100%", flexWrap: "wrap" }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nuevo contacto
          </Button>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            placeholder="Buscar por nombre, rubro, teléfono, zona…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 320 }}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Filtrar por rubro"
            style={{ minWidth: 220 }}
            value={filterRubros}
            onChange={setFilterRubros}
            options={rubroTags.map((t) => ({ value: t.nombre, label: t.nombre }))}
            maxTagCount="responsive"
          />
          <Button icon={<FileExcelOutlined />} onClick={exportExcel} disabled={!filtered.length}>
            Exportar Excel
          </Button>
        </Space>

        <Table<Maestro>
          rowKey="id"
          loading={loading}
          dataSource={filtered}
          columns={columns}
          pagination={paginacionCliente({ porDefecto: 20, etiqueta: "contacto" })}
          scroll={{ x: 1180 }}
          expandable={{
            expandRowByClick: true,
            showExpandColumn: false,
            expandedRowRender: (m) => (
              <div style={{ padding: "4px 8px" }}>
                {rubrosOf(m).length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>Rubros: </Text>
                    {rubroChips(m)}
                  </div>
                )}
                {zonaText(m) && (
                  <Paragraph style={{ marginBottom: 8 }}>
                    <Text strong>Zona: </Text>
                    <EnvironmentOutlined style={{ color: "#0369a1", margin: "0 4px" }} />
                    {zonaText(m)}
                  </Paragraph>
                )}
                <Paragraph style={{ marginBottom: 8, whiteSpace: "pre-wrap" }}>
                  <Text strong>Características: </Text>
                  {m.caracteristicas || <Text type="secondary">—</Text>}
                </Paragraph>
                <Paragraph style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}>
                  <Text strong>Notas: </Text>
                  {m.notas || <Text type="secondary">—</Text>}
                </Paragraph>
              </div>
            ),
          }}
        />

        <Modal
          title={editing ? `Editar contacto` : "Nuevo contacto"}
          open={modalOpen}
          onCancel={closeModal}
          width={760}
          destroyOnClose
          footer={[
            <Button key="close" onClick={closeModal} disabled={submitting}>
              Cancelar
            </Button>,
            <Button key="save" type="primary" loading={submitting} onClick={submit}>
              {editing ? "Guardar cambios" : "Crear"}
            </Button>,
          ]}
        >
          <Form
            form={form}
            layout="vertical"
            style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "0 16px" }}
          >
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[
                { required: true, message: "Ingresa un nombre" },
                { min: 2, message: "Mínimo 2 caracteres" },
              ]}
              style={{ gridColumn: "1 / -1" }}
            >
              <Input placeholder="Nombre del contacto o empresa" />
            </Form.Item>

            <Form.Item label="Rubros / oficios" style={{ gridColumn: "1 / -1" }}>
              <CatalogSelect
                tipo="rubro"
                mode="multiple"
                value={rubrosVal}
                onChange={(v) => setRubrosVal(v as string[])}
                placeholder="Elige uno o varios rubros"
                manageTitle="Gestionar rubros"
                onCatalogChanged={fetchRubroTags}
              />
            </Form.Item>

            <Form.Item name="telefono" label="Teléfono">
              <Input placeholder="+56 9 ..." />
            </Form.Item>

            <Form.Item name="email" label="Email" rules={[{ type: "email", message: "Email inválido" }]}>
              <Input placeholder="contacto@ejemplo.com" />
            </Form.Item>

            <Form.Item label="Zona (región / comuna / sector)" style={{ gridColumn: "1 / -1" }}>
              <GeoSelect value={geoVal} onChange={setGeoVal} />
            </Form.Item>

            <Form.Item
              name="ubicacion"
              label="Ubicación / referencia (texto libre)"
              style={{ gridColumn: "1 / -1" }}
            >
              <Input placeholder="Dirección de referencia, punto de encuentro, etc." />
            </Form.Item>

            <Form.Item
              name="caracteristicas"
              label="Características"
              tooltip="Especialidad, horario, tarifa referencial, si emite boleta, calidad del trabajo, etc."
              style={{ gridColumn: "1 / -1" }}
            >
              <Input.TextArea rows={2} placeholder="Especialidad, horario, tarifa, etc." />
            </Form.Item>

            <Form.Item
              name="notas"
              label="Notas"
              tooltip="Espacio libre para anotar cualquier cosa (historial, advertencias, recomendaciones)."
              style={{ gridColumn: "1 / -1" }}
            >
              <Input.TextArea rows={2} placeholder="Notas internas…" />
            </Form.Item>
          </Form>

          <div style={{ marginTop: 8 }}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              Fotos y documentos
            </Text>
            <AttachmentsManager
              apiBase="/agenda-contactos"
              entityId={editing?.id ?? null}
              active={modalOpen}
              emptyHint="Los adjuntos se habilitan después de crear el contacto: guárdalo y ábrelo con Editar."
            />
          </div>
        </Modal>
      </div>
    </Layout>
  );
}
